import { MagnifyingGlass, CaretRight, IconProps } from '@phosphor-icons/react'
import { Input, Dialog, DialogContent, DialogTrigger, Icon } from '@shopflo/ui'
import React, { useState, useMemo, useEffect, useRef } from 'react'
import { searchItems, SearchItem } from './search-items'
import { useUser } from 'src/lib/contexts/UserContext'
import useNavigation, { JourneyStep } from 'src/lib/hooks/useNavigation'
import { classNames } from 'src/lib/utils/helpers'
import { filterSearchItems, filterSearchItemsByRBAC, flattenSearchItems } from 'src/lib/utils/navigation-rbac'
import { shouldShowNavNewBadgeByPath } from './nav-new-badges'
import NavNewBadge from './NavNewBadge'
import { useRouter } from 'next/router'
import { motion } from 'motion/react'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'

export interface ExtendedSearchItem extends SearchItem {
  _hideParent?: boolean
  journeyStep?: JourneyStep
}

const getJourneyStepFromItem = (item: ExtendedSearchItem): JourneyStep => {
  return item.journeyStep || JourneyStep.NONE
}

const SearchComponent = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const { user } = useUser()
  const router = useRouter()
  const {
    getters: { hasOverdueInvoices, organisationData, isShopConnected }
  } = useOrganisationContext()
  const { isUserConfigured } = useUser()

  const isDisabled = hasOverdueInvoices || !isUserConfigured
  const { onboardingState } = organisationData

  const { t } = useLocale()

  const {
    state: { sidebarOpen },
    actions: { resetAndRedirect, setJourneyStep }
  } = useNavigation()

  const filteredItems = useMemo(() => {
    return filterSearchItemsByRBAC({
      searchItems,
      user,
      completedSteps: onboardingState?.completed_states,
      isShopConnected
    })
  }, [user, onboardingState?.completed_states, isShopConnected])

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return filteredItems
    return filterSearchItems(filteredItems, searchQuery.toLowerCase())
  }, [filteredItems, searchQuery])

  const flatResults = useMemo(() => {
    return flattenSearchItems(searchResults)
  }, [searchResults])

  const pathHierarchyMap = useMemo(() => {
    const map = new Map<string, string[]>()

    const buildPathHierarchy = (items: ExtendedSearchItem[], currentPath: string[] = []) => {
      items.forEach(item => {
        const itemPath = [...currentPath, item.title]
        map.set(item.path, itemPath)

        if (item.children) {
          buildPathHierarchy(item.children, itemPath)
        }
      })
    }

    buildPathHierarchy(filteredItems)
    return map
  }, [filteredItems])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault()
        event.stopPropagation()

        if (isDisabled) {
          return true
        }
        setIsOpen(true)
        return false
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isDisabled])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setSelectedIndex(prev => (prev < flatResults.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        event.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : flatResults.length - 1))
        break
      case 'Enter':
        event.preventDefault()
        const selectedItem = flatResults[selectedIndex]
        if (selectedItem) {
          handleItemClick(selectedItem)
        }
        break
      case 'Escape':
        event.preventDefault()
        closeSearch()
        break
    }
  }

  useEffect(() => {
    if (!isOpen || flatResults.length === 0) return

    const selectedItem = flatResults[selectedIndex]
    if (!selectedItem) return

    const selectedElement = itemRefs.current.get(selectedItem.path)
    const scrollContainer = scrollContainerRef.current

    if (scrollContainer && selectedElement) {
      const containerRect = scrollContainer.getBoundingClientRect()
      const elementRect = selectedElement.getBoundingClientRect()

      // Check if element is outside visible area
      const isAbove = elementRect.top < containerRect.top
      const isBelow = elementRect.bottom > containerRect.bottom

      if (isAbove || isBelow) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: isAbove ? 'start' : 'end',
          inline: 'nearest'
        })
      }
    }
  }, [selectedIndex, isOpen, flatResults])

  const handleItemClick = (item: ExtendedSearchItem) => {
    const journeyStep = getJourneyStepFromItem(item)

    if (journeyStep !== JourneyStep.NONE) {
      setJourneyStep(journeyStep)
      router.push(item.path)
    } else {
      resetAndRedirect(item.path)
    }
    closeSearch()
  }

  const closeSearch = () => {
    setIsOpen(false)
    setSearchQuery('')
    setSelectedIndex(0)
  }

  const renderSearchResultItem = (item: ExtendedSearchItem): React.ReactNode => {
    const pathHierarchy = pathHierarchyMap.get(item.path)
    const isSelected = flatResults[selectedIndex]?.path === item.path

    // Find the root parent for icon (first item in hierarchy)
    const rootParent =
      pathHierarchy && pathHierarchy.length > 1 ? filteredItems.find(parent => parent.title === pathHierarchy[0]) : null
    const IconComponent = item.icon || rootParent?.icon

    return (
      <div
        key={item.path}
        ref={el => {
          if (el) {
            itemRefs.current.set(item.path, el)
          } else {
            itemRefs.current.delete(item.path)
          }
        }}
        className={classNames(
          'flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors rounded-lg',
          isSelected ? 'bg-zinc-200/70' : 'hover:bg-zinc-200/50'
        )}
        onClick={() => handleItemClick(item)}
      >
        {IconComponent && (
          <div className='flex-shrink-0 w-5 h-5 flex items-center justify-center'>
            <IconComponent size={20} className='text-zinc-600' weight='duotone' />
          </div>
        )}

        <div className='min-w-0 flex-1'>
          {pathHierarchy && pathHierarchy.length > 1 ? (
            <div className='flex min-w-0 items-center gap-2 text-text-sm-medium text-zinc-600 hover:text-zinc-800'>
              {pathHierarchy.map((pathItem, index) => (
                <React.Fragment key={index}>
                  {index === pathHierarchy.length - 1 ? (
                    <div className='flex min-w-0 items-center gap-1'>
                      <span className='truncate font-medium'>{pathItem}</span>
                      {shouldShowNavNewBadgeByPath(item.path) && <NavNewBadge />}
                    </div>
                  ) : (
                    <span className='truncate font-medium'>{pathItem}</span>
                  )}
                  {index < pathHierarchy.length - 1 && (
                    <Icon IconComponent={CaretRight} size='xxxs' className='shrink-0 text-zinc-600' />
                  )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className='flex min-w-0 items-center gap-2 text-text-sm-medium text-zinc-600 hover:text-zinc-800'>
              <span className='truncate'>{item.title}</span>
              {shouldShowNavNewBadgeByPath(item.path) && <NavNewBadge />}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className='px-2 w-full z-10 '>
      <Dialog
        open={isOpen}
        onOpenChange={open => {
          if (isDisabled) {
            return
          }
          setIsOpen(open)
        }}
      >
        <DialogTrigger asChild>
          <div
            className={classNames(
              'cursor-pointer flex flex-col gap-2 justify-center items-start w-full shadow-[0_3px_5px_1px_rgba(244,244,245,1)]',
              isDisabled ? 'opacity-40' : ''
            )}
            id='search-component-trigger'
          >
            <div className=' flex items-center px-2 py-1 bg-zinc-400/10 rounded-lg gap-2 hover:bg-zinc-400/15 h-7 w-full'>
              <div className='flex-shrink-0 w-[18px] h-[18px] flex items-center justify-center'>
                <MagnifyingGlass className='text-zinc-400' size={18} />
              </div>
              {sidebarOpen && (
                <div className='flex items-center justify-between w-full'>
                  <p className='text-text-xs-regular text-zinc-400'>{t('search')}</p>
                  <span className='text-zinc-400 text-xs'>{t('navigation.ctrlk')}</span>
                </div>
              )}
            </div>
          </div>
        </DialogTrigger>

        <DialogContent className='max-w-[600px] h-[480px] overflow-y-hidden !p-0 !rounded-2xl bg-zinc-100/90 shadow-[0_0_0_1.5px_rgba(255,255,255,0.25)_inset,0_0_0_1.2px_rgba(0,0,0,0.04),0_54px_80px_-5px_rgba(0,0,0,0.15),0_12px_18px_0_rgba(0,0,0,0.05),0_4px_6px_0_rgba(0,0,0,0.03)] scroll'>
          <motion.div className='h-full flex flex-col'>
            <div className='p-2 rounded-t-2xl bg-zinc-100/90 '>
              <Input
                placeholder='Search any page...'
                dimension='lg'
                variant='none'
                className='p-2 bg-zinc-100/90 !justify-start [&>input]:!bg-transparent '
                startAdornment={<MagnifyingGlass className='text-zinc-500' size={20} />}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>

            <hr className='border-black/5' />

            <div className=' bg-zinc-100/90 rounded-b-2xl h-full  '>
              <div className='mr-0.5'>
                <div
                  ref={scrollContainerRef}
                  className='bg-zinc-100/90 overflow-y-scroll max-h-[420px] rounded-2xl px-2  [&::-webkit-scrollbar]:w-2
                  [&::-webkit-scrollbar-track]:rounded-full
                  [&::-webkit-scrollbar-track]:!bg-transparent
                  [&::-webkit-scrollbar-thumb]:rounded-full
                [&::-webkit-scrollbar-thumb]:bg-zinc-300'
                >
                  {flatResults.length > 0 ? (
                    <div className='py-2'>{flatResults.map(item => renderSearchResultItem(item))}</div>
                  ) : (
                    <div className='px-4 py-8 text-center text-zinc-500'>
                      {searchQuery.trim() ? 'No results found' : 'Start typing to search...'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default SearchComponent
